import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { exerciseAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AcademicCapIcon,
  ClockIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  FireIcon,
  BoltIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';

const Exercises = () => {
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    petType: '',
    search: '',
  });

  const { data: exercises, isLoading: exercisesLoading } = useQuery(
    ['exercises', filters], 
    () => exerciseAPI.getExercises(filters)
  );
  
  const { data: categories } = useQuery('categories', exerciseAPI.getCategories);
  const { data: difficulties } = useQuery('difficulties', exerciseAPI.getDifficulties);

  const categoryIcons = {
    'Basic Obedience': '🎯',
    'Agility': '🏃‍♂️',
    'Tricks': '🎪',
    'Socialization': '👥',
    'Behavioral': '🧠',
    'Mental Stimulation': '🧩',
    'Exercise & Fitness': '💪',
    'Grooming & Care': '🛁'
  };

  const difficultyColors = {
    1: 'from-green-400 to-green-500',
    2: 'from-blue-400 to-blue-500', 
    3: 'from-yellow-400 to-yellow-500',
    4: 'from-orange-400 to-orange-500',
    5: 'from-red-400 to-red-500'
  };

  const difficultyIcons = {
    1: '🌱',
    2: '🌿', 
    3: '🌸',
    4: '🔥',
    5: '⚡'
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ category: '', difficulty: '', petType: '', search: '' });
  };

  if (exercisesLoading) {
    return <LoadingSpinner text="Loading training exercises..." />;
  }

  const exercisesData = exercises?.data || [];
  const categoriesData = categories?.data || [];
  const difficultiesData = difficulties?.data || [];

  return (
    <div className="p-6 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <AcademicCapIcon className="h-8 w-8 text-playful-blue animate-bounce-slow" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-playful-blue to-playful-green bg-clip-text text-transparent">
            Training Exercises
          </h1>
          <SparklesIcon className="h-8 w-8 text-playful-green animate-wiggle" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2 text-playful-blue" />
            Find Perfect Exercises
          </h2>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-playful-blue transition-colors"
          >
            Clear All
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 form-input"
            />
          </div>

          {/* Category Filter */}
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="form-input"
          >
            <option value="">All Categories</option>
            {categoriesData.map((category) => (
              <option key={category.id} value={category.name}>
                {categoryIcons[category.name]} {category.name}
              </option>
            ))}
          </select>

          {/* Difficulty Filter */}
          <select
            value={filters.difficulty}
            onChange={(e) => handleFilterChange('difficulty', e.target.value)}
            className="form-input"
          >
            <option value="">All Difficulties</option>
            {difficultiesData.map((difficulty) => (
              <option key={difficulty.id} value={difficulty.level}>
                {difficultyIcons[difficulty.level]} {difficulty.name}
              </option>
            ))}
          </select>

          {/* Pet Type Filter */}
          <select
            value={filters.petType}
            onChange={(e) => handleFilterChange('petType', e.target.value)}
            className="form-input"
          >
            <option value="">All Pet Types</option>
            <option value="Dog">🐕 Dogs</option>
            <option value="Cat">🐱 Cats</option>
            <option value="Bird">🦜 Birds</option>
            <option value="Rabbit">🐰 Rabbits</option>
          </select>
        </div>
      </div>

      {/* Exercise Grid */}
      {exercisesData.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-playful-blue">
          <div className="animate-float">
            <AcademicCapIcon className="mx-auto h-16 w-16 text-playful-blue" />
          </div>
          <h3 className="mt-6 text-xl font-bold text-gray-900">No exercises found! 🤔</h3>
          <p className="mt-2 text-gray-600 max-w-sm mx-auto">
            Try adjusting your filters or search terms to find the perfect training exercises.
          </p>
          <button
            onClick={clearFilters}
            className="mt-6 btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exercisesData.map((exercise, index) => (
            <div
              key={exercise.id}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl hover:scale-105 transform transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Exercise Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">
                    {categoryIcons[exercise.category_name] || '🎯'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{exercise.name}</h3>
                    <p className="text-sm text-playful-blue font-medium">
                      {exercise.category_name}
                    </p>
                  </div>
                </div>
                
                {/* Difficulty Badge */}
                <div className={`
                  px-3 py-1 rounded-full text-white text-sm font-bold 
                  bg-gradient-to-r ${difficultyColors[exercise.difficulty_level]}
                  flex items-center space-x-1
                `}>
                  <span>{difficultyIcons[exercise.difficulty_level]}</span>
                  <span>{exercise.difficulty_name}</span>
                </div>
              </div>

              {/* Exercise Description */}
              <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                {exercise.description}
              </p>

              {/* Exercise Meta */}
              <div className="flex items-center justify-between mb-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <ClockIcon className="h-4 w-4" />
                  <span>{exercise.duration_minutes} min</span>
                </div>
                <div className="flex items-center space-x-1">
                  <HeartIcon className="h-4 w-4 text-red-400" />
                  <span>Level {exercise.difficulty_level}</span>
                </div>
              </div>

              {/* Action Button */}
              <Link
                to={`/app/exercises/${exercise.id}`}
                className="w-full bg-gradient-to-r from-playful-blue to-playful-green text-white font-bold py-3 px-4 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-center block"
              >
                🎯 Start Training
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Fun Stats */}
      {exercisesData.length > 0 && (
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-6 text-white">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-2">
              <FireIcon className="h-6 w-6 animate-bounce" />
              <h3 className="text-xl font-bold">Training Arsenal</h3>
              <BoltIcon className="h-6 w-6 animate-bounce" />
            </div>
            <p className="text-primary-100">
              You have <span className="font-bold text-accent-300">{exercisesData.length}</span> amazing exercises 
              ready to transform your pet into a training superstar! 🌟
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Exercises;